import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Footer from "../../components/Footer";
import {
  FaShieldAlt,
  FaFileContract,
  FaUserCheck,
  FaWhatsapp,
  FaTrashAlt,
  FaEnvelope,
  FaExclamationTriangle,
  FaLock,
  FaCheckCircle,
} from "react-icons/fa";
import "./TermsOfService.css";

const TermsOfService = () => {
  const [activeSection, setActiveSection] = useState("preamble");

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Terms of Service | Chauwk";
  }, []);

  const scrollToId = (id) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -90;
      const y =
        element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  const whatsappClauses = [
    {
      subNum: "5.1",
      title: "Connecting WhatsApp Business Accounts",
      paragraphs: [
        "Chauwk may allow businesses to connect their WhatsApp Business Accounts to the platform to access messaging, automation, customer support, and other available features. By connecting an account, you confirm that you are authorised to act on behalf of the relevant business and have the authority to grant the permissions required for the integration.",
      ],
    },
    {
      subNum: "5.2",
      title: "Compliance with WhatsApp and Meta Policies",
      paragraphs: [
        "Your use of WhatsApp through Chauwk must comply with all applicable WhatsApp Business Platform terms, messaging policies, Meta platform policies, and applicable laws. Chauwk does not own or control WhatsApp or Meta's services, and your access to those services remains subject to their respective terms and policies.",
      ],
    },
    {
      subNum: "5.3",
      title: "Customer Consent and Messaging Responsibilities",
      paragraphs: [
        "You are responsible for ensuring that you have the necessary permissions and legal basis to contact individuals through WhatsApp. You must respect opt-out requests, follow applicable messaging restrictions, use approved message templates where required, and ensure that your communications comply with WhatsApp's policies.",
        "You must not use Chauwk to send unsolicited bulk messages, spam, fraudulent communications, or other prohibited content or communications.",
      ],
    },
    {
      subNum: "5.4",
      title: "AI-Powered Features",
      paragraphs: [
        "Chauwk may provide AI-powered features that generate responses, automate conversations, process customer enquiries, or support voice-agent workflows. AI-generated outputs may occasionally be inaccurate, incomplete, or inappropriate.",
        "You are responsible for configuring and monitoring these features appropriately, reviewing outputs where necessary, and ensuring that automated interactions are suitable for your intended use. Chauwk does not guarantee that AI-generated responses will always be accurate or error-free.",
      ],
    },
    {
      subNum: "5.5",
      title: "Account Security and Authorisation",
      paragraphs: [
        "You are responsible for maintaining the security of your Chauwk account and ensuring that connected WhatsApp Business Accounts, credentials, and permissions are managed appropriately. You must promptly notify Chauwk of any suspected unauthorised access or misuse.",
        "You must not attempt to access another business's WhatsApp account without proper authorisation or use the integration to bypass platform restrictions.",
      ],
    },
    {
      subNum: "5.6",
      title: "Service Availability and Third-Party Dependencies",
      paragraphs: [
        "WhatsApp messaging and related features depend on third-party services, including Meta's infrastructure. Chauwk cannot guarantee uninterrupted availability of these third-party services, message delivery, approval of message templates, or continued access to specific WhatsApp features.",
        "Third-party outages, policy changes, account restrictions, or other circumstances outside Chauwk's reasonable control may affect the availability of the integration.",
      ],
    },
    {
      subNum: "5.7",
      title: "Suspension and Disconnection",
      paragraphs: [
        "Chauwk may restrict, suspend, or disconnect an integration where reasonably necessary to address suspected misuse, security risks, violations of these Terms, applicable law, or third-party platform policies.",
        "You may request disconnection of a WhatsApp Business Account from Chauwk. Disconnection from Chauwk does not automatically terminate your separate relationship with Meta or delete data retained independently by Meta or WhatsApp.",
      ],
    },
    {
      subNum: "5.8",
      title: "Responsibility for Business Communications",
      paragraphs: [
        "You remain responsible for the products, services, offers, statements, and commitments communicated to your customers through your connected accounts. You must ensure that your use of Chauwk does not infringe third-party rights or violate applicable consumer protection, privacy, advertising, or communications laws.",
      ],
    },
    {
      subNum: "5.9",
      title: "Changes to WhatsApp Integration",
      paragraphs: [
        "Chauwk may modify, add, or discontinue WhatsApp integration features as needed to maintain the platform, respond to technical changes, or comply with applicable third-party requirements. Where required, changes will be handled in accordance with the general provisions of these Terms.",
      ],
    },
  ];

  const dataDeletionSteps = [
    {
      subNum: "6.1",
      title: "How to Request Data Deletion",
      content: (
        <>
          <p>
            To request the deletion of your personal information, Chauwk account
            data, or information associated with a WhatsApp Business
            integration, email{" "}
            <a href="mailto:support@chauwk.com" className="terms-link-highlight">
              support@chauwk.com
            </a>{" "}
            from your registered email address.
          </p>
          <p>Please include:</p>
          <ul className="terms-bullet-list">
            <li>Your name and registered email address.</li>
            <li>Your business name, if applicable.</li>
            <li>A description of the account or data you want deleted.</li>
          </ul>
          <div className="terms-sub-callout">
            <FaLock className="terms-sub-callout-icon" />
            <div>
              <strong>Verification Note:</strong> For security, we may request
              additional information to verify your identity or authority to
              make the request.
            </div>
          </div>
        </>
      ),
    },
    {
      subNum: "6.2",
      title: "What Data Can Be Deleted?",
      content: (
        <>
          <p>
            Depending on your request and the services you use, deletion may
            include account information, connected WhatsApp account details,
            message records, and other personal information stored by Chauwk.
          </p>
          <p>
            Certain information may be retained where required by applicable
            law, for legitimate security purposes, or to resolve disputes.
          </p>
        </>
      ),
    },
    {
      subNum: "6.3",
      title: "WhatsApp Business Account Disconnection",
      content: (
        <>
          <p>
            If you are a business using Chauwk's WhatsApp integration, you may
            request that your connected WhatsApp Business Account be
            disconnected from Chauwk.
          </p>
          <p>
            Disconnecting your account from Chauwk does not automatically delete
            information held independently by Meta, WhatsApp, or the business you
            communicated with. You may need to contact those parties separately.
          </p>
        </>
      ),
    },
    {
      subNum: "6.4",
      title: "Processing Your Request",
      content: (
        <>
          <p>
            We will verify and process deletion requests in accordance with
            applicable law and our Privacy Policy. We will communicate with you
            if additional information is required.
          </p>
          <div className="terms-button-row">
            <Link to="/user-data-deletion" className="terms-action-btn">
              Visit Dedicated User Data Deletion Page &rarr;
            </Link>
          </div>
        </>
      ),
    },
  ];

  return (
    <>
      <div className="terms-container">
        {/* Hero Section */}
        <header className="terms-hero">
          <span className="terms-badge">Legal &amp; Compliance</span>
          <h1>TERMS OF SERVICE</h1>
          <p className="terms-subtitle">
            Comprehensive Terms and Conditions governing your use of Chauwk, our
            Portal, WhatsApp Business Integration, and related Services.
          </p>
          <div className="terms-divider" />
        </header>

        {/* Quick Navigation Sticky Bar */}
        <nav className="terms-nav-bar" aria-label="Terms Navigation">
          <div className="terms-nav-inner" onWheel={(e) => { if (e.deltaY !== 0) { e.currentTarget.scrollLeft += e.deltaY; } }}>
            <button
              type="button"
              className={`terms-nav-chip ${
                activeSection === "preamble" ? "active" : ""
              }`}
              onClick={() => scrollToId("preamble")}
            >
              Notice &amp; Agreement
            </button>
            <button
              type="button"
              className={`terms-nav-chip ${
                activeSection === "definitions" ? "active" : ""
              }`}
              onClick={() => scrollToId("definitions")}
            >
              1. Definitions
            </button>
            <button
              type="button"
              className={`terms-nav-chip ${
                activeSection === "service-description" ? "active" : ""
              }`}
              onClick={() => scrollToId("service-description")}
            >
              2. Service Description
            </button>
            <button
              type="button"
              className={`terms-nav-chip ${
                activeSection === "portal-use" ? "active" : ""
              }`}
              onClick={() => scrollToId("portal-use")}
            >
              3. Portal Use
            </button>
            <button
              type="button"
              className={`terms-nav-chip ${
                activeSection === "eligibility" ? "active" : ""
              }`}
              onClick={() => scrollToId("eligibility")}
            >
              4. Eligibility
            </button>
            <button
              type="button"
              className={`terms-nav-chip ${
                activeSection === "whatsapp-terms" ? "active" : ""
              }`}
              onClick={() => scrollToId("whatsapp-terms")}
            >
              5. WhatsApp Terms
            </button>
            <button
              type="button"
              className={`terms-nav-chip ${
                activeSection === "data-deletion" ? "active" : ""
              }`}
              onClick={() => scrollToId("data-deletion")}
            >
              6. Data Deletion
            </button>
            <button
              type="button"
              className={`terms-nav-chip ${
                activeSection === "grievance-contact" ? "active" : ""
              }`}
              onClick={() => scrollToId("grievance-contact")}
            >
              7. Grievance &amp; Contact
            </button>
          </div>
        </nav>

        {/* Content Wrapper */}
        <main className="terms-content-wrapper">
          {/* Preamble / Acceptance Notice Card */}
          <section id="preamble" className="terms-preamble-card">
            <div className="terms-preamble-header">
              <FaFileContract className="terms-preamble-icon" />
              <div>
                <h2>Important Notice &amp; Acceptance of Terms</h2>
                <span className="terms-preamble-tag">Legally Binding Agreement</span>
              </div>
            </div>
            <div className="terms-preamble-body">
              <p className="terms-highlight-paragraph">
                PLEASE READ THE FOLLOWING TERMS AND CONDITIONS VERY CAREFULLY AS
                YOUR USE OF THE SERVICES THEREIN ARE SUBJECT TO YOUR ACCEPTANCE OF
                AND COMPLIANCE WITH THE FOLLOWING TERMS AND CONDITIONS ("TERMS" /
                “AGREEMENT”).
              </p>
              <p>
                BY DOWNLOADING OR INSTALLING, REGISTERING, ACCESSING OR OTHERWISE
                USING THE PORTAL, YOU ACCEPT AND AGREE TO ALL OF THE TERMS AND
                CONDITIONS CONTAINED HEREIN. THESE TERMS OF USE AND CONDITIONS
                CONSTITUTES A LEGALLY BINDING AGREEMENT BETWEEN YOU AND THE
                COMPANY.
              </p>
              <p>
                WHILE USING THE PORTAL, YOU MAY BE SUBJECT TO ANY POSTED
                GUIDELINES OR RULES APPLICABLE TO THE PORTAL. ALL SUCH GUIDELINES
                OR RULES ARE HEREBY INCORPORATED BY REFERENCE INTO THESE TERMS.
              </p>
              <p>
                BY USING OR ACCESSING THE PORTAL, YOU DE-FACTO AGREE TO THE
                FOLLOWING TERMS.
              </p>
              <div className="terms-alert-box">
                <FaShieldAlt className="terms-alert-icon" />
                <p>
                  YOU ALSO REPRESENT THAT YOU HAVE READ AND UNDERSTOOD ALL OF THE
                  PROVISIONS OF THIS AGREEMENT. CHAUWK RESERVES THE RIGHT TO
                  CHANGE THE TERMS OF SERVICE AT ANY POINT OF TIME.
                </p>
              </div>
            </div>
          </section>

          {/* Section 1: Definitions */}
          <section id="definitions" className="terms-section-card">
            <div className="terms-section-header">
              <span className="terms-section-number">1</span>
              <div>
                <h2 className="terms-section-title">DEFINITIONS</h2>
                <p className="terms-section-subtitle-text">
                  Key terms and concepts used across this Agreement
                </p>
              </div>
            </div>

            <div className="terms-section-body">
              <div className="terms-definitions-grid">
                <div className="terms-def-item">
                  <div className="terms-def-term">
                    Varin Communications Private Limited
                  </div>
                  <div className="terms-def-desc">
                    A company registered under Companies Act 2021, having its
                    registered address at A-406, Trendset Rythme, Whitefields
                    Road, Kondapur, Hyderabad - 500084, India hereinafter
                    referred to as “Company”, “we”, “us” or “our”.
                  </div>
                </div>

                <div className="terms-def-item">
                  <div className="terms-def-term">“Users”, “You”, “Your”</div>
                  <div className="terms-def-desc">
                    Means any person who registers and creates a user account on
                    the Portal for availing the Services provided through the
                    Portal.
                  </div>
                </div>

                <div className="terms-def-item">
                  <div className="terms-def-term">“Online Stores”</div>
                  <div className="terms-def-desc">
                    Means web portal or app through which products and services
                    are made available to you.
                  </div>
                </div>

                <div className="terms-def-item">
                  <div className="terms-def-term">“Portal”</div>
                  <div className="terms-def-desc">
                    Means and includes both{" "}
                    <a
                      href="https://www.chauwk.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="terms-inline-link"
                    >
                      www.chauwk.com
                    </a>{" "}
                    and mobile application developed and owned by the Company, to
                    enable the Users to avail the Services of the Company.
                  </div>
                </div>

                <div className="terms-def-item">
                  <div className="terms-def-term">“User Products/Services”</div>
                  <div className="terms-def-desc">
                    Means and includes any new or old products sold or services
                    provided by the Users to other users through the Portal.
                  </div>
                </div>

                <div className="terms-def-item">
                  <div className="terms-def-term">“Chauwk”</div>
                  <div className="terms-def-desc">
                    Shall mean and include the feature on the Portal where
                    Company shall be displaying Chauwk Content and sell Chauwk
                    Products.
                  </div>
                </div>

                <div className="terms-def-item">
                  <div className="terms-def-term">“Chauwk Products”</div>
                  <div className="terms-def-desc">
                    Means and includes any products sold by the Company through
                    the Portal.
                  </div>
                </div>

                <div className="terms-def-item">
                  <div className="terms-def-term">“Chauwk Content”</div>
                  <div className="terms-def-desc">
                    Means and includes videos, educational content, gaming
                    content, write-ups, reviews, articles, and blogs created and
                    shared by the Company through the Portal.
                  </div>
                </div>

                <div className="terms-def-item">
                  <div className="terms-def-term">“User Content”</div>
                  <div className="terms-def-desc">
                    Means and includes write-ups, articles, audios, videos, and
                    photos shared by the Users through the Portal.
                  </div>
                </div>

                <div className="terms-def-item terms-def-item-full">
                  <div className="terms-def-term">“Services”</div>
                  <div className="terms-def-desc">
                    Shall mean Services offered on the Portal to the Users,
                    which shall include:
                    <ul className="terms-bullet-list">
                      <li>
                        Providing a social media network User Content Portal for
                        the User to connect with other users, for the purpose of
                        sharing User Content.
                      </li>
                      <li>Uploading Chauwk content on the Portal.</li>
                      <li>
                        Connecting with other Users socially and professionally
                        having similar interests.
                      </li>
                      <li>
                        Interacting and communicating with other users using the
                        Portal through any means, including but not limited to
                        chat, voice, and video.
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="terms-def-item terms-def-item-full">
                  <div className="terms-def-term">“User Data”</div>
                  <div className="terms-def-desc">
                    Means any data, content, details, information documents,
                    videos, audios, media, images, blogs, messages,
                    notifications, ideas, communications, replies, likes,
                    comments, maps, routes, geo-data, or material relating to
                    the User or their designs, copyrights, trademarks, patents,
                    and any intellectual property therein, any of which may be
                    generated, provided, or otherwise made accessible on or
                    through the Portal.
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 2: Service Description */}
          <section id="service-description" className="terms-section-card">
            <div className="terms-section-header">
              <span className="terms-section-number">2</span>
              <div>
                <h2 className="terms-section-title">SERVICE DESCRIPTION</h2>
                <p className="terms-section-subtitle-text">
                  Scope of platform capabilities and user interaction
                </p>
              </div>
            </div>

            <div className="terms-section-body">
              <p>
                Chauwk is a Portal which enables You to connect with other users
                socially and professionally. The Portal enables You to share and
                view the User Content that is to create and share original
                perspective of events happening around us in the society with
                other users registered on the Portal. The Portal also facilitates
                You to directly connect with other Users and share your views
                about any User Content.
              </p>
              <p>
                The Company also provides You a platform which facilitates the
                sale and purchase of various Products between Users, or the Users
                and the Company, or users and registered sellers. You may create
                different groups of users on the Portal for the purpose of
                sharing User Content and interact with other users.
              </p>
              <p>
                The Portal also provides a means for certain Users to raise
                invoices and receive payments thereof to other Users on behalf of
                their Community or for the User Products/Services provided by
                such Users. The Portal can be used to obtain data/feeds from
                various devices that would have been connected and configured by
                administrative Users.
              </p>

              {/* Supervision & Content Disclaimer */}
              <div className="terms-warning-card">
                <div className="terms-warning-header">
                  <FaExclamationTriangle className="terms-warning-icon" />
                  <strong>User Content Caution &amp; Supervision Disclaimer</strong>
                </div>
                <p>
                  You agree and understand that the Portal does not supervise
                  over any User Content shared by You through the Portal and is
                  also not involved in any way with the User Content shared by You
                  or any other users registered on the Portal. As a result, we
                  have no control over any User Content which is shared on the
                  Portal. You are requested to exercise caution and judgment
                  before posting any User Content on the Portal.
                </p>
              </div>
            </div>
          </section>

          {/* Section 3: Portal Use */}
          <section id="portal-use" className="terms-section-card">
            <div className="terms-section-header">
              <span className="terms-section-number">3</span>
              <div>
                <h2 className="terms-section-title">PORTAL USE</h2>
                <p className="terms-section-subtitle-text">
                  User conditions, policy amendments, and multi-user engagement
                </p>
              </div>
            </div>

            <div className="terms-section-body">
              <p>
                Users may use the Portal subject to acceptance of these Terms.
                Company retains the right to amend these Terms, without prior
                notice, from time to time and such revised Terms shall be
                applicable to the Users, upon the posting of updated Terms on the
                Portal.
              </p>
              <p>
                We may highlight or summarize the key changes for the User’s
                reference and the User shall be required to re-accept the revised
                Terms to continue availing the Services of the Portal.
              </p>
              <p>
                Company has a right to engage with multiple Users at the same
                time for provision of Services or for engagement in same or
                similar activity.
              </p>
            </div>
          </section>

          {/* Section 4: Eligibility */}
          <section id="eligibility" className="terms-section-card">
            <div className="terms-section-header">
              <span className="terms-section-number">4</span>
              <div>
                <h2 className="terms-section-title">ELIGIBILITY</h2>
                <p className="terms-section-subtitle-text">
                  Age limits, authority, and membership criteria
                </p>
              </div>
            </div>

            <div className="terms-section-body">
              <div className="terms-eligibility-highlight">
                <FaUserCheck className="terms-eligibility-icon" />
                <div>
                  <strong>Age Criterion: Minimum 18 Years of Age</strong>
                  <p>
                    To be eligible for availing the Services, You must be at least
                    18 years old. The Portal is not available to those who are
                    temporarily or indefinitely suspended.
                  </p>
                </div>
              </div>

              <p>
                By registering to use the Portal, You represent and warrant that
                you are at least 18 years of age and that You are fully able,
                competent and authorized to enter into the terms, conditions,
                obligations, representations and warranties set forth in these
                Terms.
              </p>
              <p>
                To join or subscribe to a Publisher’s paid video membership as a
                Viewer, or provide a membership as a Publisher, you must be at
                least 18 years old.
              </p>
            </div>
          </section>

          {/* Section 5: WhatsApp Business Integration and Messaging Terms */}
          <section id="whatsapp-terms" className="terms-section-card">
            <div className="terms-section-header">
              <span className="terms-section-number">5</span>
              <div>
                <h2 className="terms-section-title">
                  WHATSAPP BUSINESS INTEGRATION AND MESSAGING TERMS
                </h2>
                <p className="terms-section-subtitle-text">
                  Rules for connecting accounts, automated messaging, and AI agents
                </p>
              </div>
            </div>

            <div className="terms-section-body">
              <div className="terms-sub-intro-card">
                <FaWhatsapp className="terms-whatsapp-icon" />
                <p>
                  Please review these Terms of Service carefully before connecting
                  your WhatsApp Business Account to Chauwk. These terms govern your
                  use of messaging, automation, AI features, and related services
                  provided on our platform.
                </p>
              </div>

              <div className="terms-subclauses-list">
                {whatsappClauses.map((clause) => (
                  <article key={clause.subNum} className="terms-subclause-card">
                    <div className="terms-subclause-header">
                      <span className="terms-subclause-num">{clause.subNum}</span>
                      <h3 className="terms-subclause-title">{clause.title}</h3>
                    </div>
                    <div className="terms-subclause-body">
                      {clause.paragraphs.map((p, idx) => (
                        <p key={idx}>{p}</p>
                      ))}
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>

          {/* Section 6: User Data Deletion Instructions */}
          <section id="data-deletion" className="terms-section-card">
            <div className="terms-section-header">
              <span className="terms-section-number">6</span>
              <div>
                <h2 className="terms-section-title">
                  USER DATA DELETION INSTRUCTIONS
                </h2>
                <p className="terms-section-subtitle-text">
                  Transparent procedures for requesting personal data deletion
                </p>
              </div>
            </div>

            <div className="terms-section-body">
              <p>
                Chauwk, operated by Varin Communications Private Limited,
                respects your privacy and provides a way to request the deletion
                of personal information associated with our services, including
                our WhatsApp Business integration.
              </p>

              <div className="terms-subclauses-list">
                {dataDeletionSteps.map((step) => (
                  <article key={step.subNum} className="terms-subclause-card">
                    <div className="terms-subclause-header">
                      <span className="terms-subclause-num">{step.subNum}</span>
                      <h3 className="terms-subclause-title">{step.title}</h3>
                    </div>
                    <div className="terms-subclause-body">{step.content}</div>
                  </article>
                ))}
              </div>
            </div>
          </section>

          {/* Section 7: Grievance, Contact & Communication */}
          <section id="grievance-contact" className="terms-section-card">
            <div className="terms-section-header">
              <span className="terms-section-number">7</span>
              <div>
                <h2 className="terms-section-title">
                  GRIEVANCE, CONTACT &amp; COMMUNICATION
                </h2>
                <p className="terms-section-subtitle-text">
                  Support channels, redressal office, and registered details
                </p>
              </div>
            </div>

            <div className="terms-section-body">
              <p>
                For any product related issues, redemption queries, data deletion
                requests, or grievances, please reach out to us:
              </p>

              <div className="terms-contact-grid">
                <div className="terms-contact-box">
                  <FaEnvelope className="terms-contact-icon" />
                  <h4>Email Support</h4>
                  <p>
                    <a href="mailto:support@chauwk.com">support@chauwk.com</a>
                  </p>
                  <p>
                    <a href="mailto:hello@chauwk.com">hello@chauwk.com</a>
                  </p>
                </div>

                <div className="terms-contact-box">
                  <FaShieldAlt className="terms-contact-icon" />
                  <h4>Registered Office</h4>
                  <p>
                    <strong>Varin Communications Private Limited</strong>
                  </p>
                  <p>
                    A-406, Trendset Rythme, Whitefields Road, Kondapur, Hyderabad –
                    500084, India
                  </p>
                </div>

                <div className="terms-contact-box">
                  <FaCheckCircle className="terms-contact-icon" />
                  <h4>Privacy &amp; Compliance</h4>
                  <p>Review our comprehensive privacy practices:</p>
                  <p>
                    <Link
                      to="/privacy-policy"
                      className="terms-link-highlight font-semibold"
                    >
                      Privacy Policy &rarr;
                    </Link>
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Brand Signature Footer Card */}
          <div className="terms-brand-stamp">
            <img
              src="/chauwk-logo.png"
              alt="Chauwk Logo"
              className="terms-brand-logo"
            />
            <p className="terms-brand-copyright">
              &copy; {new Date().getFullYear()} Varin Communications Private
              Limited. All rights reserved.
            </p>
          </div>
        </main>
      </div>

      <Footer />
    </>
  );
};

export default TermsOfService;
